// Razorpay integration utility
declare global {
  interface Window {
    Razorpay: any;
  }
}

export interface RazorpayOptions {
  key: string;
  amount: number;
  currency: string;
  name: string;
  description: string;
  order_id: string;
  handler: (response: RazorpayResponse) => void;
  prefill?: {
    name?: string;
    email?: string;
    contact?: string;
  };
  notes?: {
    [key: string]: string;
  };
  theme?: {
    color?: string;
  };
  modal?: {
    ondismiss?: () => void;
  };
}

export interface RazorpayResponse {
  razorpay_payment_id: string;
  razorpay_order_id: string;
  razorpay_signature: string;
}

// Enhanced failure response interface
export interface RazorpayFailureResponse {
  error: {
    code: string;
    description: string;
    source: string;
    step: string;
    reason: string;
    metadata?: {
      [key: string]: any;
    };
  };
  payment_id?: string;
  order_id?: string;
}

// Payment failure status enum
export enum PaymentFailureStatus {
  PAYMENT_FAILED = 'payment_failed',
  ORDER_NOT_FOUND = 'order_not_found',
  INVALID_AMOUNT = 'invalid_amount',
  CURRENCY_MISMATCH = 'currency_mismatch',
  CARD_DECLINED = 'card_declined',
  INSUFFICIENT_FUNDS = 'insufficient_funds',
  EXPIRED_CARD = 'expired_card',
  INVALID_CARD = 'invalid_card',
  NETWORK_ERROR = 'network_error',
  TIMEOUT = 'timeout',
  USER_CANCELLED = 'user_cancelled',
  MODAL_DISMISSED = 'modal_dismissed',
  SCRIPT_LOAD_FAILED = 'script_load_failed',
  INITIALIZATION_FAILED = 'initialization_failed',
  UNKNOWN_ERROR = 'unknown_error'
}

// Failure status text mapping
export const FAILURE_STATUS_TEXT: Record<PaymentFailureStatus, string> = {
  [PaymentFailureStatus.PAYMENT_FAILED]: 'Payment processing failed. Please try again.',
  [PaymentFailureStatus.ORDER_NOT_FOUND]: 'Payment order not found. Please contact support.',
  [PaymentFailureStatus.INVALID_AMOUNT]: 'Invalid payment amount. Please check the amount and try again.',
  [PaymentFailureStatus.CURRENCY_MISMATCH]: 'Currency mismatch. Please contact support.',
  [PaymentFailureStatus.CARD_DECLINED]: 'Your card was declined. Please try a different payment method.',
  [PaymentFailureStatus.INSUFFICIENT_FUNDS]: 'Insufficient funds in your account. Please try a different payment method.',
  [PaymentFailureStatus.EXPIRED_CARD]: 'Your card has expired. Please use a different payment method.',
  [PaymentFailureStatus.INVALID_CARD]: 'Invalid card details. Please check and try again.',
  [PaymentFailureStatus.NETWORK_ERROR]: 'Network error occurred. Please check your internet connection and try again.',
  [PaymentFailureStatus.TIMEOUT]: 'Payment request timed out. Please try again.',
  [PaymentFailureStatus.USER_CANCELLED]: 'Payment was cancelled by you.',
  [PaymentFailureStatus.MODAL_DISMISSED]: 'Payment modal was closed without completing the transaction.',
  [PaymentFailureStatus.SCRIPT_LOAD_FAILED]: 'Payment system failed to load. Please refresh the page and try again.',
  [PaymentFailureStatus.INITIALIZATION_FAILED]: 'Payment system initialization failed. Please try again.',
  [PaymentFailureStatus.UNKNOWN_ERROR]: 'An unexpected error occurred. Please try again or contact support.'
};

export interface PaymentOrder {
  id: string;
  amount: number;
  currency: string;
  receipt: string;
}

class RazorpayService {
  private isLoaded = false;
  private scriptId = 'razorpay-script';

  // Enhanced error mapping function
  private mapRazorpayError(error: any): PaymentFailureStatus {
    if (!error) return PaymentFailureStatus.UNKNOWN_ERROR;

    // Handle Razorpay specific error codes
    if (error.error && error.error.code) {
      const code = error.error.code.toLowerCase();
      
      switch (code) {
        case 'payment_failed':
          return PaymentFailureStatus.PAYMENT_FAILED;
        case 'order_not_found':
          return PaymentFailureStatus.ORDER_NOT_FOUND;
        case 'invalid_amount':
          return PaymentFailureStatus.INVALID_AMOUNT;
        case 'currency_mismatch':
          return PaymentFailureStatus.CURRENCY_MISMATCH;
        case 'card_declined':
          return PaymentFailureStatus.CARD_DECLINED;
        case 'insufficient_funds':
          return PaymentFailureStatus.INSUFFICIENT_FUNDS;
        case 'expired_card':
          return PaymentFailureStatus.EXPIRED_CARD;
        case 'invalid_card':
          return PaymentFailureStatus.INVALID_CARD;
        case 'network_error':
          return PaymentFailureStatus.NETWORK_ERROR;
        case 'timeout':
          return PaymentFailureStatus.TIMEOUT;
        case 'user_cancelled':
          return PaymentFailureStatus.USER_CANCELLED;
        default:
          return PaymentFailureStatus.UNKNOWN_ERROR;
      }
    }

    // Handle common error patterns
    if (error.message) {
      const message = error.message.toLowerCase();
      
      if (message.includes('network') || message.includes('connection')) {
        return PaymentFailureStatus.NETWORK_ERROR;
      }
      if (message.includes('timeout')) {
        return PaymentFailureStatus.TIMEOUT;
      }
      if (message.includes('cancelled') || message.includes('canceled')) {
        return PaymentFailureStatus.USER_CANCELLED;
      }
      if (message.includes('script') || message.includes('load')) {
        return PaymentFailureStatus.SCRIPT_LOAD_FAILED;
      }
      if (message.includes('initialization') || message.includes('init')) {
        return PaymentFailureStatus.INITIALIZATION_FAILED;
      }
    }

    return PaymentFailureStatus.UNKNOWN_ERROR;
  }

  // Get failure status text
  getFailureStatusText(status: PaymentFailureStatus): string {
    return FAILURE_STATUS_TEXT[status] || FAILURE_STATUS_TEXT[PaymentFailureStatus.UNKNOWN_ERROR];
  }

  // Get detailed failure information
  getFailureDetails(error: any): {
    status: PaymentFailureStatus;
    text: string;
    details: string;
    code?: string;
    description?: string;
  } {
    const status = this.mapRazorpayError(error);
    const text = this.getFailureStatusText(status);
    
    let details = text;
    let code: string | undefined;
    let description: string | undefined;

    if (error?.error) {
      code = error.error.code;
      description = error.error.description;
      
      if (description) {
        details = `${text} Details: ${description}`;
      }
    } else if (error?.message) {
      details = `${text} Error: ${error.message}`;
    }

    return {
      status,
      text,
      details,
      code,
      description
    };
  }

  // Load Razorpay script
  private loadScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      if (this.isLoaded) {
        resolve();
        return;
      }

      // Check if script already exists
      const existingScript = document.getElementById(this.scriptId);
      if (existingScript) {
        this.isLoaded = true;
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.id = this.scriptId;
      script.src = 'https://checkout.razorpay.com/v1/checkout.js';
      script.async = true;
      script.onload = () => {
        this.isLoaded = true;
        resolve();
      };
      script.onerror = () => {
        const error = new Error('Failed to load Razorpay script');
        reject(error);
      };

      document.head.appendChild(script);
    });
  }

  // Initialize payment with enhanced error handling
  async initializePayment(options: RazorpayOptions): Promise<void> {
    try {
      await this.loadScript();

      if (!window.Razorpay) {
        throw new Error('Razorpay not available');
      }

      const rzp = new window.Razorpay({
        ...options,
        modal: {
          ondismiss: () => {
            console.log('Payment modal dismissed');
            options.modal?.ondismiss?.();
          },
          ...options.modal
        }
      });

      rzp.open();
    } catch (error) {
      console.error('Error initializing Razorpay payment:', error);
      throw error;
    }
  }

  // Create payment order (this should be called from your backend)
  async createOrder(amount: number, currency: string = 'INR', receipt?: string): Promise<PaymentOrder> {
    try {
      // In real implementation, this would call your backend API
      // which would then call Razorpay's API to create an order
      const response = await fetch('/api/payments/create-order', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify({
          amount,
          currency,
          receipt: receipt || `receipt_${Date.now()}`
        })
      });

      if (!response.ok) {
        throw new Error('Failed to create payment order');
      }

      return await response.json();
    } catch (error) {
      console.error('Error creating payment order:', error);
      throw error;
    }
  }

  // Verify payment signature (this should be called from your backend)
  async verifyPayment(paymentData: RazorpayResponse): Promise<boolean> {
    try {
      const response = await fetch('/api/payments/verify', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        },
        body: JSON.stringify(paymentData)
      });

      if (!response.ok) {
        throw new Error('Payment verification failed');
      }

      const result = await response.json();
      return result.verified;
    } catch (error) {
      console.error('Error verifying payment:', error);
      throw error;
    }
  }

  // Format amount for Razorpay (amount in paise)
  formatAmount(amount: number): number {
    return Math.round(amount * 100); // Convert to paise
  }

  // Get Razorpay key from environment
  getRazorpayKey(): string {
    const key = process.env.REACT_APP_RAZORPAY_KEY_ID;
    if (!key) {
      throw new Error('Razorpay key not configured');
    }
    return key;
  }
}

// Export singleton instance
export const razorpayService = new RazorpayService();

// Utility function to get failure status in text mode for debugging
export const getFailureStatusText = (error: any): string => {
  return razorpayService.getFailureDetails(error).text;
};

// Utility function to get detailed failure information for debugging
export const getDetailedFailureInfo = (error: any) => {
  return razorpayService.getFailureDetails(error);
};

// Utility function to simulate different failure scenarios for testing
export const simulateFailureScenario = (scenario: PaymentFailureStatus): any => {
  const mockErrors: Record<PaymentFailureStatus, any> = {
    [PaymentFailureStatus.PAYMENT_FAILED]: {
      error: {
        code: 'payment_failed',
        description: 'Payment processing failed due to technical issues',
        source: 'gateway',
        step: 'payment_processing',
        reason: 'gateway_error'
      }
    },
    [PaymentFailureStatus.CARD_DECLINED]: {
      error: {
        code: 'card_declined',
        description: 'Your card was declined by the bank',
        source: 'bank',
        step: 'card_verification',
        reason: 'insufficient_funds'
      }
    },
    [PaymentFailureStatus.INSUFFICIENT_FUNDS]: {
      error: {
        code: 'insufficient_funds',
        description: 'Insufficient balance in your account',
        source: 'bank',
        step: 'balance_check',
        reason: 'low_balance'
      }
    },
    [PaymentFailureStatus.EXPIRED_CARD]: {
      error: {
        code: 'expired_card',
        description: 'Your card has expired',
        source: 'card',
        step: 'card_validation',
        reason: 'expired'
      }
    },
    [PaymentFailureStatus.NETWORK_ERROR]: {
      message: 'Network error occurred while processing payment'
    },
    [PaymentFailureStatus.TIMEOUT]: {
      message: 'Payment request timed out'
    },
    [PaymentFailureStatus.USER_CANCELLED]: {
      message: 'Payment was cancelled by user'
    },
    [PaymentFailureStatus.MODAL_DISMISSED]: {
      type: 'modal_dismissed',
      message: 'Payment modal was dismissed by user'
    },
    [PaymentFailureStatus.SCRIPT_LOAD_FAILED]: {
      message: 'Failed to load Razorpay script'
    },
    [PaymentFailureStatus.INITIALIZATION_FAILED]: {
      message: 'Failed to initialize Razorpay payment'
    },
    [PaymentFailureStatus.ORDER_NOT_FOUND]: {
      error: {
        code: 'order_not_found',
        description: 'Payment order not found',
        source: 'system',
        step: 'order_lookup',
        reason: 'invalid_order'
      }
    },
    [PaymentFailureStatus.INVALID_AMOUNT]: {
      error: {
        code: 'invalid_amount',
        description: 'Invalid payment amount',
        source: 'validation',
        step: 'amount_validation',
        reason: 'invalid_amount'
      }
    },
    [PaymentFailureStatus.CURRENCY_MISMATCH]: {
      error: {
        code: 'currency_mismatch',
        description: 'Currency mismatch in payment',
        source: 'validation',
        step: 'currency_validation',
        reason: 'currency_error'
      }
    },
    [PaymentFailureStatus.INVALID_CARD]: {
      error: {
        code: 'invalid_card',
        description: 'Invalid card details provided',
        source: 'validation',
        step: 'card_validation',
        reason: 'invalid_card'
      }
    },
    [PaymentFailureStatus.UNKNOWN_ERROR]: {
      message: 'An unexpected error occurred'
    }
  };

  return mockErrors[scenario] || mockErrors[PaymentFailureStatus.UNKNOWN_ERROR];
};

// Helper function to initiate payment (legacy version for dues)
export const initiatePayment = async (
  amount: number,
  orderId: string,
  description: string,
  userDetails?: {
    name?: string;
    email?: string;
    phone?: string;
  },
  onSuccess?: (response: RazorpayResponse) => void,
  onFailure?: (error: any) => void,
  onDismiss?: () => void
) => {
  try {
    const razorpayKey = razorpayService.getRazorpayKey();
    
    const options: RazorpayOptions = {
      key: razorpayKey,
      amount: razorpayService.formatAmount(amount),
      currency: 'INR',
      name: 'SFL Football League',
      description,
      order_id: orderId,
      prefill: userDetails,
      theme: {
        color: '#0d6efd'
      },
      handler: (response: RazorpayResponse) => {
        console.log('Payment successful:', response);
        onSuccess?.(response);
      },
      modal: {
        ondismiss: () => {
          console.log('Payment modal dismissed');
          const dismissError = { type: 'modal_dismissed', message: 'Payment modal was dismissed by user' };
          const failureDetails = razorpayService.getFailureDetails(dismissError);
          console.log('Payment modal dismissed - Failure details:', failureDetails);
          onDismiss?.();
        }
      }
    };

    await razorpayService.initializePayment(options);
  } catch (error) {
    console.error('Payment initiation failed:', error);
    const failureDetails = razorpayService.getFailureDetails(error);
    console.log('Payment failure details:', failureDetails);
    onFailure?.(failureDetails);
  }
};

// Helper function to initiate payment with custom key (for tournament payments)
export const initiatePaymentWithKey = async (
  amount: number,
  orderId: string,
  description: string,
  razorpayKey: string,
  userDetails?: {
    name?: string;
    email?: string;
    phone?: string;
  },
  onSuccess?: (response: RazorpayResponse) => void,
  onFailure?: (error: any) => void,
  onDismiss?: () => void
) => {
  try {
    const options: RazorpayOptions = {
      key: razorpayKey,
      amount: razorpayService.formatAmount(amount),
      currency: 'INR',
      name: 'SFL Football League',
      description,
      order_id: orderId,
      prefill: userDetails,
      theme: {
        color: '#0d6efd'
      },
      handler: (response: RazorpayResponse) => {
        console.log('Payment successful:', response);
        onSuccess?.(response);
      },
      modal: {
        ondismiss: () => {
          console.log('Payment modal dismissed');
          const dismissError = { type: 'modal_dismissed', message: 'Payment modal was dismissed by user' };
          const failureDetails = razorpayService.getFailureDetails(dismissError);
          console.log('Payment modal dismissed - Failure details:', failureDetails);
          onDismiss?.();
        }
      }
    };

    await razorpayService.initializePayment(options);
  } catch (error) {
    console.error('Payment initiation failed:', error);
    const failureDetails = razorpayService.getFailureDetails(error);
    console.log('Payment failure details:', failureDetails);
    onFailure?.(failureDetails);
  }
}; 